import type { FC } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { useFeatureFlags } from "@jti/commerce";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { useSelector } from "@jti/store/reduxHooks";
import { useGetProfileQuery } from "@jti/store/slices/profileApiSlice";
import { Telephone, useFormContext, useTranslations } from "@jti/ui";

import { OrderAttributesSlot } from "@checkout-v2/features/OrderAttributes";
import { FISCAL_CODE_ATTRIBUTE_KEY } from "@checkout-v2/features/OrderAttributes/const";
import { telephoneFieldFactory, TextInputField } from "@checkout-v2/formFields";
import type { ShippingFormFieldsConfig, ShippingFormFieldsDefaultValues } from "@checkout-v2/forms";
import { ContactInfo } from "@checkout-v2/shared/components/ContactInfo";
import { checkIsEmptyTelephoneField } from "@checkout-v2/shared/utils/telephoneUtils/toTelephoneField";

import { RecipientCheckbox } from "./RecipientCheckbox";
import { RecipientToggle } from "./RecipientToggle";

import styles from "./BasicContactDetailsSection.module.scss";

export type BasicContactDetailsSectionProps = {
  formFieldsConfig: Pick<ShippingFormFieldsConfig, "firstName" | "lastName" | "telephone" | "shouldUseProfileData">;
  defaultValues: Pick<ShippingFormFieldsDefaultValues, "firstName" | "lastName" | "telephone">;
};

export const BasicContactDetailsSection: FC<BasicContactDetailsSectionProps> = ({
  formFieldsConfig: {
    firstName: firstNameFieldConfig,
    lastName: lastNameFieldConfig,
    telephone: telephoneFieldConfig,
    shouldUseProfileData: shouldUseProfileDataFieldConfig,
  },
  defaultValues: { telephone: telephoneDefaultValue },
}) => {
  const { __ } = useTranslations();
  const { isReversedNameOrder } = useConfig();
  const { ffCheckoutV2EnableIteration2 } = useFeatureFlags();

  const form = useFormContext();

  const { data: profileData } = useGetProfileQuery();
  const checkoutData = useSelector((state) => state.checkoutV2.data);

  const enteredFiscalCode =
    typeof checkoutData?.orderAttributes?.[FISCAL_CODE_ATTRIBUTE_KEY] === "string"
      ? checkoutData.orderAttributes[FISCAL_CODE_ATTRIBUTE_KEY].trim()
      : "";
  const profileFiscalCode = profileData?.extra?.[FISCAL_CODE_ATTRIBUTE_KEY]?.trim() || enteredFiscalCode;
  const shouldUseProfileData = form.watch(shouldUseProfileDataFieldConfig.name);

  const nameFields = (
    isReversedNameOrder
      ? [
          { fieldConfig: lastNameFieldConfig, dataTestId: "lastNameInput" },
          { fieldConfig: firstNameFieldConfig, dataTestId: "firstNameInput" },
        ]
      : [
          { fieldConfig: firstNameFieldConfig, dataTestId: "firstNameInput" },
          { fieldConfig: lastNameFieldConfig, dataTestId: "lastNameInput" },
        ]
  ).filter(({ fieldConfig }) => !fieldConfig.isHidden);

  const isProfilePhoneRequiredButMissing =
    telephoneFieldConfig.isRequired &&
    telephoneFieldConfig.isHidden &&
    shouldUseProfileData &&
    checkIsEmptyTelephoneField(telephoneFieldConfig.savedDefaultValue);

  const isPhoneNumberValid = telephoneFieldConfig.isPhoneNumberValid;

  return (
    <div className={styles.container}>
      {!shouldUseProfileDataFieldConfig.isHidden &&
        (ffCheckoutV2EnableIteration2 ? (
          <RecipientToggle
            fieldConfig={shouldUseProfileDataFieldConfig}
            firstNameSavedDefault={firstNameFieldConfig.savedDefaultValue}
            lastNameSavedDefault={lastNameFieldConfig.savedDefaultValue}
            telephoneSavedDefault={telephoneFieldConfig.savedDefaultValue}
          />
        ) : (
          <RecipientCheckbox
            fieldConfig={shouldUseProfileDataFieldConfig}
            firstNameSavedDefault={firstNameFieldConfig.savedDefaultValue}
            lastNameSavedDefault={lastNameFieldConfig.savedDefaultValue}
            telephoneSavedDefault={telephoneFieldConfig.savedDefaultValue}
            telephoneDefaultValue={telephoneDefaultValue}
          />
        ))}

      <OrderAttributesSlot slotTarget="contactDetails_before" />

      {!shouldUseProfileData && (
        <div className={styles.names}>
          {nameFields.map(({ fieldConfig, dataTestId }) => (
            <TextInputField key={dataTestId} fieldConfig={fieldConfig} dataTestId={dataTestId} />
          ))}
        </div>
      )}

      {shouldUseProfileData && (
        <ContactInfo
          firstname={firstNameFieldConfig.savedDefaultValue}
          lastname={lastNameFieldConfig.savedDefaultValue}
          telephone={
            telephoneFieldConfig.isHidden
              ? telephoneFieldFactory.toTelephoneString(telephoneFieldConfig.savedDefaultValue)
              : ""
          }
          fiscalCode={profileFiscalCode}
        />
      )}

      {isProfilePhoneRequiredButMissing && (
        <AlertBanner
          tone="warning"
          data-testid="message-box"
          description={__("cart.checkout.warning.missing_phone_number")}
        />
      )}

      {!isPhoneNumberValid && !isProfilePhoneRequiredButMissing && (
        <AlertBanner
          tone="warning"
          data-testid="invalid-phone-message-box"
          description={__("cart.checkout.warning.invalid_phone_number")}
        />
      )}

      {(!telephoneFieldConfig.isHidden || !shouldUseProfileData || isProfilePhoneRequiredButMissing) && (
        <Telephone
          fieldName={telephoneFieldConfig.fieldName}
          isRequired={telephoneFieldConfig.isRequired}
          isInfoMessageVisible={isProfilePhoneRequiredButMissing}
          isPhoneFieldFullWidth={true}
          placeholder={__("internationalPhone.placeholder")}
        />
      )}

      <OrderAttributesSlot slotTarget="contactDetails_after" />
    </div>
  );
};
